import React, { useState } from 'react';
import { ShieldCheck, Flame, Compass, ChevronRight, Info, Award, Globe } from 'lucide-react';
import { WealthCalculationInputs } from '../../types';
import { calculateFIRERetirement } from '../../lib/wealthMathEngine';
import { RETIREMENT_COUNTRY_PACKS } from '../../data/wealthData';

export const FIRERetirementView: React.FC = () => {
  const [inputs, setInputs] = useState<WealthCalculationInputs>({
    initialPrincipal: 50000,
    periodicContribution: 1200,
    nominalReturnRatePct: 7.5,
    inflationRatePct: 2.5,
    annualFeePct: 0.20,
    timeHorizonYears: 20,
    compoundingFrequency: 'monthly',
    contributionFrequency: 'monthly',
    contributionTiming: 'END_OF_PERIOD',
    annualRetirementSpending: 45000,
    withdrawalRatePct: 4.0,
    currentAge: 32,
    retirementAge: 52,
    currency: 'USD',
  });

  const [selectedPackId, setSelectedPackId] = useState<string>('pack-us-2026');

  const result = calculateFIRERetirement(inputs);
  const selectedPack = RETIREMENT_COUNTRY_PACKS.find((p) => p.id === selectedPackId) || RETIREMENT_COUNTRY_PACKS[0];

  return (
    <div className="max-w-7xl mx-auto space-y-8 pb-16">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-purple-900 via-violet-900 to-slate-900 rounded-2xl p-6 sm:p-8 text-white shadow-xl">
        <div className="space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-semibold border border-purple-500/30">
            <Flame className="w-3.5 h-3.5" />
            <span>YamooPay FIRE & Retirement Intelligence Core</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            Financial Independence (FIRE) & Retirement Drawdown Engine
          </h1>
          <p className="text-slate-300 text-sm max-w-2xl leading-relaxed">
            Explore retirement accumulation trajectories, target FI numbers based on custom withdrawal rates, decumulation sustainability horizons, and official regional tax-advantaged account rules.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Controls Column */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-6">
          <h2 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3">FIRE & Retirement Inputs</h2>

          <div className="space-y-4 text-xs text-slate-700">
            <div>
              <label className="block font-semibold text-slate-900 mb-1">Target Annual Retirement Spending ($)</label>
              <input
                type="number"
                value={inputs.annualRetirementSpending}
                onChange={(e) =>
                  setInputs((prev) => ({ ...prev, annualRetirementSpending: parseFloat(e.target.value) || 0 }))
                }
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-bold text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Withdrawal Rate (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={inputs.withdrawalRatePct}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, withdrawalRatePct: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <span className="text-[10px] text-slate-500">Heuristic: 4% = 25x annual expenses</span>
              </div>

              <div>
                <label className="block font-semibold text-slate-900 mb-1">Invested Assets Today ($)</label>
                <input
                  type="number"
                  value={inputs.initialPrincipal}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, initialPrincipal: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Monthly Contribution ($)</label>
                <input
                  type="number"
                  value={inputs.periodicContribution}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, periodicContribution: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-900 mb-1">Assumed Return (%)</label>
                <input
                  type="number"
                  step="0.1"
                  value={inputs.nominalReturnRatePct}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, nominalReturnRatePct: parseFloat(e.target.value) || 0 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Current Age</label>
                <input
                  type="number"
                  value={inputs.currentAge || 30}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, currentAge: parseInt(e.target.value) || 30 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <div>
                <label className="block font-semibold text-slate-900 mb-1">Target FI Age</label>
                <input
                  type="number"
                  value={inputs.retirementAge || 50}
                  onChange={(e) =>
                    setInputs((prev) => ({ ...prev, retirementAge: parseInt(e.target.value) || 50 }))
                  }
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results & Country Pack Column */}
        <div className="lg:col-span-7 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-purple-900 text-white rounded-2xl p-5 shadow-sm space-y-2">
              <div className="text-xs text-purple-200 font-medium">Target FI Nest Egg</div>
              <div className="text-2xl font-extrabold text-purple-300">
                ${result.fireTargetAmount?.toLocaleString()}
              </div>
              <div className="text-[11px] text-purple-200">
                Based on ${inputs.annualRetirementSpending?.toLocaleString()}/yr @ {inputs.withdrawalRatePct}% withdrawal
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-2">
              <div className="text-xs text-slate-500 font-medium">Estimated Horizon to FI</div>
              <div className="text-2xl font-extrabold text-slate-900">
                {result.fireYearsToTarget} years
              </div>
              <div className="text-[11px] text-slate-500">
                Target Age: ~{(inputs.currentAge || 30) + (result.fireYearsToTarget || 0)} years old
              </div>
            </div>
          </div>

          {/* Regional Retirement Accounts Selector */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-purple-600" />
                <h3 className="text-sm font-bold text-slate-900">Official Country Retirement Rules (2026)</h3>
              </div>
              <select
                value={selectedPackId}
                onChange={(e) => setSelectedPackId(e.target.value)}
                className="bg-slate-50 text-xs font-semibold rounded-lg px-2.5 py-1 border border-slate-200 text-slate-800"
              >
                {RETIREMENT_COUNTRY_PACKS.map((pack) => (
                  <option key={pack.id} value={pack.id}>
                    {pack.countryName} ({pack.taxYear})
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-3">
              {selectedPack.supportedAccounts.map((acc) => (
                <div key={acc.code} className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between text-xs">
                  <div>
                    <div className="font-bold text-slate-900">{acc.name}</div>
                    <div className="text-slate-500 text-[11px]">Tax Treatment: <span className="font-semibold text-purple-800">{acc.taxTreatment}</span></div>
                  </div>
                  <div className="text-right">
                    <div className="font-extrabold text-slate-900">${acc.annualContributionLimit.toLocaleString()}/yr</div>
                    <div className="text-[10px] text-slate-500">{acc.limitSource}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
